import React, { useEffect, useState } from 'react';
import { getOverdueSummary, getOverdueRdv, getOverdueDevis, getOverdueRelances, getOverdueChantiers, getOverdueClients } from '../lib/api';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { AlertTriangle, Calendar, FileCheck2, HardHat, Users } from 'lucide-react';
import { OverdueTab } from '../components/retard/OverdueTab';
import { OVERDUE_CATEGORIES } from '../components/retard/shared';

const KpiCard = ({ label, value, color, icon: Icon, active, onClick, testId }) => (
    <button
        onClick={onClick}
        className={`text-left border rounded-sm p-4 bg-white transition-all ${active ? 'border-[#E85A24] shadow-sm' : 'border-[#E4E4E7] hover:border-[#E85A24]/60'}`}
        data-testid={testId}
    >
        <div className="flex items-center justify-between mb-2">
            <span className="text-xs uppercase tracking-wide text-[#52525B]">{label}</span>
            <Icon className="w-4 h-4" style={{ color }} />
        </div>
        <div className="text-3xl font-bold" style={{ color }}>{value}</div>
    </button>
);

const ICON_MAP = {
    rdv_sans_devis: AlertTriangle,
    devis_sans_retour: FileCheck2,
    relances_en_retard: Calendar,
    chantiers_a_receptionner: HardHat,
    clients_silencieux: Users,
};

export const EnRetardPage = () => {
    const [summary, setSummary] = useState({
        rdv_sans_devis: 0,
        devis_sans_retour: 0,
        relances_en_retard: 0,
        chantiers_a_receptionner: 0,
        clients_silencieux: 0,
        total: 0,
    });
    const [activeTab, setActiveTab] = useState('rdv');

    const loadSummary = async () => {
        try {
            const res = await getOverdueSummary();
            setSummary(res.data || {});
        } catch (err) {
            console.warn('Failed to load overdue summary:', err?.response?.status || err?.message);
        }
    };

    useEffect(() => {
        loadSummary();
        const i = setInterval(loadSummary, 60000);
        return () => clearInterval(i);
    }, []);

    return (
        <div className="space-y-4 sm:space-y-6" data-testid="en-retard-page">
            <div>
                <h1 className="text-2xl sm:text-3xl font-bold text-[#0A0A0A] tracking-tight flex items-center gap-2 sm:gap-3">
                    <AlertTriangle className="w-6 h-6 sm:w-7 sm:h-7 text-[#DC2626]" /> En retard
                </h1>
                <p className="text-sm text-[#52525B] mt-1">
                    {summary.total} prospect{summary.total > 1 ? 's' : ''} ou dossier{summary.total > 1 ? 's' : ''} nécessite{summary.total > 1 ? 'nt' : ''} votre attention
                </p>
            </div>

            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-5 gap-2 sm:gap-3">
                {OVERDUE_CATEGORIES.map((cat) => {
                    const Icon = ICON_MAP[cat.summary] || AlertTriangle;
                    return (
                        <KpiCard
                            key={cat.value}
                            label={cat.label}
                            value={summary[cat.summary] ?? 0}
                            color={cat.color}
                            icon={Icon}
                            active={activeTab === cat.value}
                            onClick={() => setActiveTab(cat.value)}
                            testId={`retard-kpi-${cat.value}`}
                        />
                    );
                })}
            </div>

            <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
                <div className="overflow-x-auto -mx-3 sm:mx-0 px-3 sm:px-0">
                    <TabsList className="bg-[#F4F4F5] p-1 rounded-sm inline-flex flex-nowrap sm:flex-wrap h-auto w-max sm:w-auto">
                        {OVERDUE_CATEGORIES.map((cat) => (
                            <TabsTrigger
                                key={cat.value}
                                value={cat.value}
                                className="data-[state=active]:bg-white rounded-sm whitespace-nowrap"
                                data-testid={`retard-tab-${cat.value}`}
                            >
                                {cat.label}
                                {summary[cat.summary] > 0 && (
                                    <span className="ml-2 bg-[#DC2626] text-white text-[10px] font-bold px-1.5 py-0.5 rounded-full">
                                        {summary[cat.summary]}
                                    </span>
                                )}
                            </TabsTrigger>
                        ))}
                    </TabsList>
                </div>

                <TabsContent value="rdv" className="mt-4">
                    <OverdueTab
                        loader={getOverdueRdv}
                        emptyMsg="Aucun RDV sans devis — bon travail !"
                        snoozeEntityType="dossier"
                        showDateField="date_rdv"
                        columnLabel="Date RDV"
                        exportCategory="rdv_sans_devis"
                    />
                </TabsContent>
                <TabsContent value="devis" className="mt-4">
                    <OverdueTab
                        loader={getOverdueDevis}
                        emptyMsg="Aucun devis en attente de retour"
                        snoozeEntityType="dossier"
                        showDateField="date_devis_envoi"
                        columnLabel="Devis envoyé le"
                        exportCategory="devis_sans_retour"
                    />
                </TabsContent>
                <TabsContent value="relances" className="mt-4">
                    <OverdueTab
                        loader={getOverdueRelances}
                        emptyMsg="Aucune relance en retard"
                        snoozeEntityType="relance"
                        showDateField="date_prevue"
                        columnLabel="Prévue le"
                        exportCategory="relances"
                        reassignEnabled={false}
                    />
                </TabsContent>
                <TabsContent value="chantiers" className="mt-4">
                    <OverdueTab
                        loader={getOverdueChantiers}
                        emptyMsg="Aucun chantier à réceptionner"
                        snoozeEntityType="dossier"
                        showDateField="date_fin_chantier"
                        columnLabel="Fin chantier"
                        exportCategory="chantiers"
                    />
                </TabsContent>
                <TabsContent value="silence" className="mt-4">
                    <OverdueTab
                        loader={getOverdueClients}
                        emptyMsg="Aucun client silencieux"
                        snoozeEntityType="prospect"
                        showLastContact
                        exportCategory="clients_silencieux"
                        reassignEnabled={false}
                    />
                </TabsContent>
            </Tabs>
        </div>
    );
};
